<template>
  <div class="flex flex-col w-full mx-auto gm-import-step5">
    <section class="gm-import-step5__assignment-group" aria-label="List and tag assignment">
    <div
      class="gm-import-step5__field-group"
      :class="{
        'mb-1':
          stepSaved &&
          localModel.leadsLists &&
          localModel.leadsLists.length === 0,
        'mb-4': !(
          stepSaved &&
          localModel.leadsLists &&
          localModel.leadsLists.length === 0
        ),
      }"
    >
      <label id="gm-import-step5-list-label" class="font-semibold text-sm text-grey-700 my-2">
        {{ $t('frontend_automations_select_list') }}
      </label>
      <treeselect
        class="h-10 custom-treeselect treeselect-add-new"
        :class="{
          'error text-danger': stepSaved && localModel.leadsLists && localModel.leadsLists.length === 0
        }"
        v-model="localModel.leadsLists"
        :multiple="true"
        :options="listData"
        :closeOnSelect="closeOnSelect"
        :clearOnSelect="clearOnSelect"
        :clearable="clearable"
        @select="listSelected"
        :valueFormat="'object'"
        :normalizer="normalizer"
        :placeholder="$t('frontend_automations_select_list')"
        instance-id="gm-import-step5-list"
        aria-labelledby="gm-import-step5-list-label"
      />
      <p
        v-if="
          stepSaved &&
          localModel.leadsLists &&
          localModel.leadsLists.length === 0
        "
        class="text-sm text-danger"
      >
        {{ $t('frontend_groovemail_mail_lead_new_multiple_list_required') }}
      </p>
    </div>
    <div class="mb-6 gm-import-step5__field-group">
      <label id="gm-import-step5-tag-label" class="font-semibold text-sm text-grey-700 my-2">
        {{ $t('frontend_automations_select_tag') }}
      </label>
      <treeselect
        class="custom-treeselect treeselect-add-new"
        v-model="localModel.leadsTags"
        :multiple="true"
        :closeOnSelect="closeOnSelect"
        :clearOnSelect="clearOnSelect"
        :clearable="clearable"
        :options="tagsData"
        @select="tagSelected"
        :valueFormat="'object'"
        :normalizer="normalizer"
        :placeholder="$t('frontend_automations_select_tag')"
        instance-id="gm-import-step5-tag"
        aria-labelledby="gm-import-step5-tag-label"
      />
    </div>
    </section>

    <section class="gm-import-step5__import-group" aria-label="CSV import and field mapping">
      <div
        :class="{
          'mb-1': stepSaved && (!localModel.leadUploadFiles || !localModel.leadUploadFiles.length),
          'mb-4': !(stepSaved && (!localModel.leadUploadFiles || !localModel.leadUploadFiles.length)),
        }"
      >
        <div class="flex mb-2 gm-import-step5__file-actions">
          <span class="w-1/2"
            ><form enctype="multipart/form-data">
              <label
                class="upload-leads-btn gm-import-step5__upload-trigger"
                for="upload-leads"
                :style="leadsButtonViaImport"
              >
                {{ $t('frontend_groovemail_mail_lead_new_multiple_enter_email_via_import') }}
              </label>
              <input
                id="upload-leads"
                type="file"
                accept=".csv"
                @change="onFileChange"
              />
            </form>
          </span>

          <gd-button
            class="w-1/2 ml-2 gm-import-step5__sample-button"
            size="auto"
            variant="primary"
            @click="downloadSampleForAddingLeads()"
          >
            <span>
              {{ $t('frontend_groovemail_admin_block_sample_file') }}
              <span
                class="fa fa-download ml-2"
                aria-hidden="true"
              ></span
            ></span>
          </gd-button>
        </div>
        <p
          v-if="stepSaved && (!localModel.leadUploadFiles || !localModel.leadUploadFiles.length)"
          class="text-sm text-danger"
        >
          {{ $t('frontend_groovemail_mail_lead_new_multiple_select_file_email_to_import') }}
        </p>
        <p class="text-sm text-grey-700 font-semibold mb-4">
          <span class="fa fa-info-circle mr-1"></span>
          <span> {{ $t('frontend_groovemail_import_leads_only_csv_allowed') }} </span>
        </p>
      </div>

      <div class="mb-2" v-if="currentlySelectedFileName">
        <p class="text-grey-700 text-sm font-semibold">
          <span class="text-deep-dark text-sm font-bold">
            {{ $t('frontend_groovemail_admin_import_user_approval_csv_name_header') }}:
          </span> {{ currentlySelectedFileName }}
        </p>
      </div>

      <div class="mb-2" v-if="currentlySelectedFileRecords">
        <p class="text-grey-700 text-sm font-semibold">
          <span class="text-deep-dark text-sm font-bold">
            {{ $t('frontend_groovemail_admin_import_user_approval_csv_file_of_leads_total') }}:
          </span> {{ currentlySelectedFileRecords }}
        </p>
      </div>

      <div class="bg-white rounded shadow gm-import-step5__mapping-card">
        <div 
          class="flex flex-col border border-grey-500 rounded-t"
          v-if="localModel.importedLeadHeaders && localModel.importedLeadHeaders.length"
          >
          <div class="flex justify-between p-3 gm-import-step5__mapping-columns">
            <div class="w-5/12 gm-import-step5__mapping-column-heading">
              <p class="text-md text-dark uppercase font-semibold">{{ $t('frontend_groovemail_leads_import_multiple_leads_header') }}</p>
            </div>
            <div class="w-7/12 gm-import-step5__mapping-column-heading">
              <p class="text-md text-dark uppercase font-semibold">{{ $t('frontend_groovemail_leads_import_multiple_leads_field') }}</p>
            </div>
          </div>
        </div>

        <div class="flex flex-col border-b border-r border-l border-grey-500"
          :class="{'mb-2 rounded-b': index === localModel.importedLeadHeaders.length - 1}"
          v-for="(header, index) in localModel.importedLeadHeaders" :key="index"
        >
          <template v-if="header.name">
            <div class="flex justify-between px-3 py-2 gm-import-step5__mapping-row">
              <div class="w-5/12 gm-import-step5__mapping-name">
                <p :id="'gm-import-step5-header-' + index">{{ header.name }}</p>
              </div>
              <div class="w-6/12 flex items-center gm-import-step5__mapping-selector">
                <treeselect
                  class="h-10 custom-treeselect treeselect-add-new custom-multilevel-select"
                  :multiple="false"
                  v-model="header.key"
                  :options="fieldsData"
                  :closeOnSelect="closeOnSelect"
                  :clearable="clearable"
                  :clearOnSelect="clearOnSelect"
                  :isDefaultExpanded="true"
                  :defaultExpandLevel="1"
                  :showCount="true"
                  :disableBranchNodes="true"
                  @input="fieldForColumnSelected(header.key, index)"
                  :placeholder="
                    $t('frontend_groovemail_contacts_step2_select_field')
                  "
                  :instance-id="'gm-import-step5-field-' + index"
                  :aria-labelledby="'gm-import-step5-header-' + index"
                />
              </div>
              <div class="w-1/12 flex justify-center items-center gm-import-step5__unmap">
                <div 
                  :title="$t('frontend_groovemail_import_unmap_header_title')"
                  v-if="header.key" class="cursor-pointer"
                  @click="removeKeyForHeader(index)"
                  >
                  <i class="fas fa-undo ml-2 text-grey-700"></i>
                </div>
              </div>
            </div>
          </template>
        </div>
      </div>
      <p
        v-if="
          localModel.importedLeadHeaders &&
          localModel.importedLeadHeaders.length &&
          stepSaved && !checkFieldIsSelected({ fieldtype: 'standard', name: 'email' })
        "
        class="text-sm text-danger"
      >
        {{ $t('frontend_groovemail_leads_import_multiple_leads_atleast_one_key_mapped_to_email') }}
      </p>
    </section>
  </div>
</template>

<script>
import axios from 'axios';

import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import showAlert from '@/components/sweet-alert-popup.js';
import themeConfig from '@/theme/themeConfig';

export default {
  name: 'step5',

  props: {
    model: {
      required: true,
    },
    lists: {
      required: true,
    },
    tags: {
      required: true,
    },
    existingfield_options: {
      required: true,
    }
  },

  data() {
    return {
      stepSaved: false,
      clearable: false,
      closeOnSelect: true,
      clearOnSelect: true,
      currentHeaderSelectIndex: -1,
      currentlySelectedFileRecords: 0,
      currentlySelectedFileName: '',
      localModel: this.model,
      normalizer(node) {
        return {
          id: node.id,
          label: node.name,
        };
      },
    };
  },

  components: {
    Treeselect,
  },

  methods: {
    validate() {
      this.stepSaved = true;
      let isValid = false;
      const emailKeyMapped = this.checkFieldIsSelected(
        {
          fieldtype: 'standard',
          name: 'email',
        }
      );
      if (
        this.localModel.leadsLists &&
        this.localModel.leadsLists.length &&
        this.localModel.leadUploadFiles &&
        this.localModel.leadUploadFiles.length &&
        emailKeyMapped && this.localModel.fileId
      ) {
        isValid = true;
      }
      this.$emit('on-validate', isValid);
      return isValid;
    },

    listSelected(node) {
      if (node.id === 'createListOption1') {
        this.$emit('openCreateListModal');
      }
    },

    tagSelected(node) {
      if (node.id === 'createTagOption1') {
        this.$emit('openCreateTagModal');
      }
    },

    checkFieldIsSelected(element) {
      let disableField = false;
      if (this.localModel.importedLeadHeaders && this.localModel.importedLeadHeaders.length) {
        this.localModel.importedLeadHeaders.forEach((fieldEle) => {
          if (element.fieldtype === 'standard' && element.name === fieldEle.key) {
            disableField = true;
          }
          if (element.fieldtype === 'custom' && element.identifier === fieldEle.key) {
            disableField = true;
          }
        });
      }
      return disableField;
    },

    async downloadSampleForAddingLeads() {
      this.$emit('showLoader');
      try {
        await axios
          .get('/user-leads/sample-user-leads-csv-download')
          .then((response) => {
            const fileURL = window.URL.createObjectURL(new Blob([response.data]));
            const fileLink = document.createElement('a');
            fileLink.href = fileURL;
            fileLink.setAttribute('download', `multi_leads.csv`);
            document.body.appendChild(fileLink);
            fileLink.click();
          });
      } catch (e) {
        console.log(e);
      }
      this.$emit('hideLoader');
    },

    async onFileChange(e) {
      this.localModel.leadUploadFiles = e.target.files || e.dataTransfer.files;
      if (
        this.localModel.leadUploadFiles &&
        this.localModel.leadUploadFiles[0] &&
        (this.localModel.leadUploadFiles[0].type === 'text/csv' ||
          this.localModel.leadUploadFiles[0].name.includes('.csv'))
      ) {
        this.localModel.importedLeadHeaders = [];
        this.$emit('showLoader');
        try {
          const validateRes = await axios.post('/import-leads/upload-leads-csv', this.getFileValidateDataPayload());
          if (validateRes.data.status === 200) {
            this.onFileValidationSuccess();
            this.localModel.fileId = validateRes.data.data.id;
            this.currentlySelectedFileName = this.localModel.leadUploadFiles[0].name;
            this.currentlySelectedFileRecords = validateRes.data.data.row_count;
          } else {
            showAlert({
              iconType: 'alert-error',
              html : `
                <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
                <p class="text-sm text-deep-dark"> ${ validateRes.data.message }</p>
              `,
              showCancelButton: false,
              showCloseButton: true,
              confirmButtonColor: themeConfig.appPrimaryColor,
              confirmButtonText: this.$t(
                'frontend_close'
              ),
            });
            this.initializeFileData();
          }
          this.$emit('hideLoader');
        } catch (err) {
          this.$emit('hideLoader');
        }
      } else {
        showAlert({
          iconType: 'alert-error',
          html : `
            <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
            <p class="text-lg font-bold text-deep-dark mb-3"> ${ this.$t('frontend_groovemail_smtp_mail_sent_not_allowed_message') }</p>
            <p class="text-sm text-deep-dark"> ${ this.$t('frontend_groovemail_import_leads_only_csv_allowed') }</p>
          `,
          showCancelButton: false,
          showCloseButton: true,
          confirmButtonColor: themeConfig.appPrimaryColor,
          confirmButtonText: this.$t(
            'frontend_close'
          ),
        });
        this.initializeFileData();
      }
    },

    getFileValidateDataPayload() {
      const dataForFileValidate = new FormData();
      dataForFileValidate.append('csv', this.localModel.leadUploadFiles[0]);
      dataForFileValidate.append('csvName', this.localModel.leadUploadFiles[0].name);
      return dataForFileValidate;
    },

    initializeFileData() {
      document.getElementById('upload-leads').value = '';
      this.localModel.fileId = null;
      this.currentlySelectedFileName = '';
      this.currentlySelectedFileRecords = 0;
      this.localModel.importedLeadHeaders = [];
    },

    onFileValidationSuccess() {
      this.$emit('showLoader');
      this.$papa.parse(this.localModel.leadUploadFiles[0], {
        header: false,
        skipEmptyLines: 'greedy',
        complete: (results) => {
          this.$emit('hideLoader');
          results.data[0].forEach((ele) => {
            this.localModel.importedLeadHeaders.push({
              name: ele,
              key: null,
              type: ''
            });
          });
        }
      });
    },

    async fieldForColumnSelected(currentField, headerIndex) {
      this.currentHeaderSelectIndex = headerIndex;
      if (currentField !== 'createNewCustomField1') {
        let fieldChoosen = this.existingfield_options[0].fields.find(
          element => element.name === currentField
        );
        if (fieldChoosen) {
          this.localModel.importedLeadHeaders[headerIndex].key = fieldChoosen.name;
          this.localModel.importedLeadHeaders[headerIndex].type = 'standard';
        }
        else {
          fieldChoosen = this.existingfield_options[1].fields.find(
            element => element.identifier === currentField
          );
          if (fieldChoosen) {
            this.localModel.importedLeadHeaders[headerIndex].key = fieldChoosen.identifier;
            this.localModel.importedLeadHeaders[headerIndex].type = 'custom';
          }
        }
      } else {
        await this.$emit('openCreateFieldModal');
        this.localModel.importedLeadHeaders[headerIndex].key = null;
      }
    },

    removeKeyForHeader(headerIndex) {
      this.localModel.importedLeadHeaders[headerIndex].key = null;
      this.localModel.importedLeadHeaders[headerIndex].type = "";
    }
  },

  computed: {
    listData() {
      const listData = [];
      if (this.lists && this.lists.length) {
        this.lists.forEach((element) => {
          if (element.is_default !== 1) {
            listData.push(element);
          }
        });
      }
      const listIndex = listData.findIndex((listEle) => {
        return listEle.id === 'createListOption1';
      });
      if (listIndex < 0) {
        listData.push({
          id: 'createListOption1',
          name: this.$t('frontend_groovemail_mail_list_newlist'),
        });
      }
      return listData;
    },

    tagsData() {
      const tagsData = [];
      if (this.tags && this.tags.length) {
        this.tags.forEach((element) => {
          tagsData.push(element);
        });
      }
      const tagIndex = tagsData.findIndex((tagEle) => {
        return tagEle.id === 'createTagOption1';
      });
      if (tagIndex < 0) {
        tagsData.push({
          id: 'createTagOption1',
          name: this.$t('frontend_groovemail_mail_tag_new_tag'),
        });
      }
      return tagsData;
    },

    leadsButtonViaImport() {
      return {
        '--import-email-text': this.$t(
          'frontend_groovemail_mail_lead_new_multiple_enter_email_via_import'
        ),
      };
    },

    // This function initializes a formFieldsData array with three objects, each representing a group of fields.
    // It then iterates over the existingfield_options array and updates the formFieldsData array with the fields from each element.
    // If an element has a fields property with a non-zero length, it iterates over the fields array and pushes a new child object
    // to the appropriate children array in the formFieldsData array.
    fieldsData() {
      const formFieldsData = [
        {
          id: 'standard',
          label: this.$t('frontend_groovemail_forms_step1_standard_fields_title'),
          children: [],
        },
        {
          id: 'custom',
          label: this.$t('frontend_groovemail_forms_step1_custom_fields_title'),
          children: [],
        },
        {
          id: 'create_field',
          label: this.$t('frontend_groovemail_customfields_index_new_custom'),
          children: [
            {
              id: 'createNewCustomField1',
              label: this.$t('frontend_groovemail_customfields_index_new_custom'),
              isDisabled: false,
            },
          ],
        },
      ];

      this.existingfield_options.forEach((option, index) => {
        if (option.fields && option.fields.length) {
          option.fields.forEach((element) => {
            const child = {
              id: element.name || element.identifier,
              label: element.identifier,
              isDisabled: this.checkFieldIsSelected(element),
            };
            formFieldsData[index].children.push(child);
          });
        }
      });

      return formFieldsData;
    }
  },

  watch: {
    localModel() {
      this.$emit('updateModel', this.localModel);
    },
  },
};
</script>

<style lang="scss">
#upload-leads {
  opacity: 0;
  position: absolute;
  z-index: -1;
}

.upload-leads-btn {
  margin: 0px !important;
  display: block;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  position: relative;
  width: auto;
  cursor: pointer;
  border: 0;
  height: 40px;
  border-radius: 6px;
  outline: 0;
  font-size: 16px;
  text-align: center;
  line-height: 40px;
  font-weight: 600 !important;
}

.upload-leads-btn:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.upload-leads-btn:after {
  text-align: center;
  position: absolute;
  top: 0;
  left: 0;
  font-weight: 600 !important;
  width: auto;
  display: block;
  content: var(--import-email-text) !important;
}

.custom-lead-data-select {
  .vue-treeselect__menu-container {
    .vue-treeselect__list {
      max-height: 171px !important;
    }
  }
}
</style>

<style scoped>
.gm-import-step5 {
  width: 100%;
  max-width: 720px;
  min-width: 0;
  padding: 4px 0 28px;
}

.gm-import-step5__assignment-group,
.gm-import-step5__import-group {
  min-width: 0;
  padding: 20px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gm-import-step5__assignment-group {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
  margin-bottom: 18px;
}

.gm-import-step5__field-group {
  min-width: 0;
  margin-bottom: 0;
}

.gm-import-step5__field-group label {
  display: block;
  margin: 0 0 8px;
  color: var(--gs-foreground);
}

.gm-import-step5 p.text-danger {
  color: var(--gs-danger);
}

.gm-import-step5 ::v-deep .vue-treeselect.error .vue-treeselect__control {
  border-color: var(--gs-danger);
}

.gm-import-step5__file-actions {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.gm-import-step5__file-actions > span,
.gm-import-step5__file-actions > * {
  width: auto;
  min-width: 0;
  margin: 0;
}

.gm-import-step5__upload-trigger {
  width: 100%;
  min-height: 40px;
  height: 40px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border-strong);
  border-radius: var(--gs-radius-md);
  box-shadow: none;
  transition: border-color 150ms ease, background-color 150ms ease;
}

.gm-import-step5__upload-trigger:hover {
  background: var(--gs-surface-subtle);
  border-color: var(--gs-primary);
  box-shadow: none;
}

.gm-import-step5__upload-trigger:after {
  content: none;
}

.gm-import-step5__sample-button {
  justify-content: center;
  min-height: 40px;
  color: var(--gs-foreground);
  background: var(--gs-surface);
  border: 1px solid var(--gs-border-strong);
  border-radius: var(--gs-radius-md);
  box-shadow: none;
}

.gm-import-step5__sample-button:hover,
.gm-import-step5__sample-button:focus {
  color: var(--gs-foreground);
  background: var(--gs-surface-subtle);
  border-color: var(--gs-primary);
  box-shadow: none;
}

.gm-import-step5__mapping-card {
  min-width: 0;
  overflow: visible;
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
  box-shadow: none;
}

.gm-import-step5__mapping-card > div {
  margin-bottom: 0;
  border: 0;
  border-radius: 0;
}

.gm-import-step5__mapping-card > div + div {
  border-top: 1px solid var(--gs-border);
}

.gm-import-step5__mapping-columns,
.gm-import-step5__mapping-row {
  display: grid;
  grid-template-columns: minmax(0, 0.75fr) minmax(0, 1fr) 44px;
  gap: 12px;
  align-items: center;
}

.gm-import-step5__mapping-columns {
  padding: 12px 16px;
  background: var(--gs-surface-subtle);
}

.gm-import-step5__mapping-column-heading {
  width: auto;
  min-width: 0;
}

.gm-import-step5__mapping-column-heading p {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 12px;
  letter-spacing: .04em;
}

.gm-import-step5__mapping-row {
  min-width: 0;
  padding: 14px 16px;
}

.gm-import-step5__mapping-name,
.gm-import-step5__mapping-selector {
  width: auto;
  min-width: 0;
}

.gm-import-step5__mapping-name p {
  margin: 0;
  overflow-wrap: anywhere;
  color: var(--gs-foreground);
  font-size: 14px;
  font-weight: 650;
  line-height: 1.45;
}

.gm-import-step5__mapping-selector {
  width: 100%;
}

.gm-import-step5__unmap {
  width: 44px;
  min-width: 44px;
  min-height: 44px;
  margin: 0;
}

.gm-import-step5__unmap > div {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  min-height: 44px;
  color: var(--gs-muted-foreground);
  border-radius: var(--gs-radius-md);
}

.gm-import-step5__unmap > div:hover {
  color: var(--gs-danger);
  background: var(--gs-surface-subtle);
}

.gm-import-step5 ::v-deep .vue-treeselect__control {
  min-height: 40px;
  border-color: var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gm-import-step5 ::v-deep .vue-treeselect--focused .vue-treeselect__control {
  border-color: var(--gs-active-border);
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gm-import-step5 ::v-deep .vue-treeselect__label {
  white-space: normal;
  overflow-wrap: anywhere;
}

@media (max-width: 640px) {
  .gm-import-step5 {
    padding: 0 0 20px;
  }

  .gm-import-step5__assignment-group,
  .gm-import-step5__import-group {
    padding: 18px;
    border-radius: var(--gs-radius-md);
  }

  .gm-import-step5__assignment-group,
  .gm-import-step5__file-actions {
    grid-template-columns: minmax(0, 1fr);
  }

  .gm-import-step5__mapping-columns {
    display: none;
  }

  .gm-import-step5__mapping-card > div:nth-child(2) {
    border-top: 0;
  }

  .gm-import-step5__upload-trigger,
  .gm-import-step5__sample-button,
  .gm-import-step5 ::v-deep .vue-treeselect,
  .gm-import-step5 ::v-deep .vue-treeselect__control,
  .gm-import-step5 ::v-deep .vue-treeselect__option {
    min-height: 44px;
    height: auto;
  }

  .gm-import-step5__mapping-row {
    grid-template-columns: minmax(0, 1fr) 44px;
    gap: 10px;
    padding: 16px;
  }

  .gm-import-step5__mapping-name {
    grid-column: 1 / -1;
  }

  .gm-import-step5__mapping-selector {
    grid-column: 1;
  }

  .gm-import-step5__unmap {
    grid-column: 2;
  }
}
</style>
